import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { ApiError } from '@/lib/api/errors'
import { updateVenueReservationSetting } from '@/lib/venue/reservationSettingApi'
import type {
  VenueReservationSettingUpdateInput,
  VenueReservationSettingView,
} from '@/lib/venue/reservationSettingTypes'

import { venueReservationSettingQueryKey } from './useVenueReservationSetting'
import { venueReservationSettingHistoriesQueryKey } from './useVenueReservationSettingHistories'

/**
 * `PUT .../reservation-setting` (#758, 백엔드 #757 §2).
 *
 * 🚨 성공 응답(= 조회와 같은 shape, 전환 후 상태)을 조회 쿼리 캐시에 **직접 반영**한다 — 재조회하지
 * 않는다(`useSaveVenueProfile` 선례와 동일 이유: Reader 복제 지연이면 방금 전환한 값이 아니라 옛
 * 값을 받아올 수 있다. 백엔드도 "화면이 곧바로 재조회할 필요가 없다"고 명시한다 — #757 §2).
 * 이력이 늘었을 수 있어(멱등 전환이 아니면 1행 추가) 이력 쿼리는 무효화한다.
 */
export function useUpdateVenueReservationSetting(venueId: string) {
  const queryClient = useQueryClient()

  return useMutation<VenueReservationSettingView, ApiError, VenueReservationSettingUpdateInput>({
    mutationFn: (input) => updateVenueReservationSetting(venueId, input),
    onSuccess: (data) => {
      queryClient.setQueryData(venueReservationSettingQueryKey(venueId), data)
      void queryClient.invalidateQueries({ queryKey: venueReservationSettingHistoriesQueryKey(venueId) })
    },
  })
}
